import { useId, useMemo, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { apiBaseUrl, isApiError, ResearchClient, type ResearchFilter } from '../api/client';
import type { ResearchExport, ResearchMetricValue, ResearchRole, ResearchSummary } from '../api/types';
import { LanguageSwitch } from '../components/Layout';
import { useToast } from '../components/Toasts';
import { Chip, ErrorNotice, Loading, StatCard, ZinqLogo } from '../components/ui';
import { useI18n, type Locale } from '../i18n';
import { catalogText, findCatalogMetric, findCatalogTask, RESEARCH_CATALOG } from '../lib/catalog';
import { formatSeconds } from '../lib/dates';
import { saveBlob } from '../lib/download';
import { errorMessageKey } from '../lib/errors';
import { formatDecimal, formatPercent } from '../lib/money';
import { readStorage, STORAGE_KEYS, writeStorage } from '../lib/storage';
import { useLoad } from '../lib/useLoad';

const EXPORTS: readonly ResearchExport[] = ['sessions', 'tasks', 'answers', 'events'];
const ROLES: readonly ResearchRole[] = ['USER', 'MERCHANT'];
const STUDY_LABEL = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * The research team's results dashboard (/research, not in the merchant navigation): the export
 * key (RESEARCH_EXPORT_KEY) is typed in, kept in this tab's sessionStorage only and sent as
 * x-research-key to the ZINQ API. Numbers come from GET /research/summary; labels from the
 * research catalog.
 */
export function ResearchPage() {
  const { t } = useI18n();
  const [key, setKey] = useState<string | null>(() => readStorage('session', STORAGE_KEYS.researchKey));
  const [keyError, setKeyError] = useState(false);

  const saveKey = (value: string | null): void => {
    setKey(value);
    writeStorage('session', STORAGE_KEYS.researchKey, value);
  };

  return (
    <div className="research">
      <header className="research-top">
        <div className="brand">
          <ZinqLogo size={34} />
          <div>
            <div className="brand-name">ZINQ</div>
            <div className="brand-sub">{t('research.subtitle')}</div>
          </div>
        </div>
        <LanguageSwitch />
      </header>
      <main id="main" className="research-main" tabIndex={-1}>
        <h1>{t('research.title')}</h1>
        {key ? (
          <ResearchDashboard
            researchKey={key}
            onInvalidKey={() => {
              saveKey(null);
              setKeyError(true);
            }}
            onForget={() => saveKey(null)}
          />
        ) : (
          <KeyForm
            invalid={keyError}
            onSubmit={(k) => {
              setKeyError(false);
              saveKey(k);
            }}
          />
        )}
      </main>
    </div>
  );
}

function KeyForm({ invalid, onSubmit }: { invalid: boolean; onSubmit: (key: string) => void }) {
  const { t } = useI18n();
  const id = useId();
  const [value, setValue] = useState('');
  const submit = (e: FormEvent): void => {
    e.preventDefault();
    const k = value.trim();
    if (k) onSubmit(k);
  };
  return (
    <form className="card key-form" onSubmit={submit}>
      <label className="field" htmlFor={id}>
        <span className="field-label">{t('research.key')}</span>
      </label>
      <input
        id={id}
        type="password"
        autoComplete="off"
        spellCheck={false}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        aria-describedby={`${id}-hint${invalid ? ` ${id}-err` : ''}`}
        aria-invalid={invalid ? true : undefined}
        data-testid="research-key"
        required
      />
      <p id={`${id}-hint`} className="muted small">
        {t('research.keyHint')}
      </p>
      {invalid ? (
        <p id={`${id}-err`} className="field-error" role="alert">
          {t('research.keyInvalid')}
        </p>
      ) : null}
      <button type="submit" className="btn btn--primary" data-testid="research-open">
        {t('research.open')}
      </button>
    </form>
  );
}

function ResearchDashboard({
  researchKey,
  onInvalidKey,
  onForget,
}: {
  researchKey: string;
  onInvalidKey: () => void;
  onForget: () => void;
}) {
  const { t } = useI18n();
  const notify = useToast();
  const labelId = useId();
  const fromId = useId();
  const toId = useId();
  const client = useMemo(() => new ResearchClient(apiBaseUrl(), researchKey), [researchKey]);
  const [labelDraft, setLabelDraft] = useState(RESEARCH_CATALOG.studyLabel);
  const [fromDraft, setFromDraft] = useState('');
  const [toDraft, setToDraft] = useState('');
  const [filter, setFilter] = useState<ResearchFilter>({ studyLabel: RESEARCH_CATALOG.studyLabel });
  const [downloading, setDownloading] = useState<ResearchExport | null>(null);
  const query: ResearchFilter = {
    studyLabel: filter.studyLabel || undefined,
    from: filter.from || undefined,
    to: filter.to || undefined,
  };

  const { state, reload } = useLoad(
    async () => {
      try {
        return await client.summary(query);
      } catch (e) {
        if (isApiError(e) && e.status === 401) onInvalidKey();
        throw e;
      }
    },
    [client, filter.studyLabel, filter.from, filter.to],
  );

  const download = async (kind: ResearchExport): Promise<void> => {
    setDownloading(kind);
    try {
      const file = await client.exportCsv(kind, query);
      saveBlob(file.blob, file.filename);
      notify(t('research.downloaded', { file: file.filename }));
    } catch (e) {
      if (isApiError(e) && e.status === 401) onInvalidKey();
      else notify(t(errorMessageKey(e)), 'danger');
    } finally {
      setDownloading(null);
    }
  };

  const labelValid = labelDraft === '' || STUDY_LABEL.test(labelDraft);
  const rangeValid = !fromDraft || !toDraft || fromDraft <= toDraft;
  const draftChanged =
    labelDraft !== (filter.studyLabel ?? '') ||
    fromDraft !== (filter.from ?? '') ||
    toDraft !== (filter.to ?? '');

  let body: ReactNode;
  if (state.status === 'loading') body = <Loading />;
  else if (state.status === 'error') body = <ErrorNotice error={state.error} onRetry={() => reload()} />;
  else body = <ResearchSummaryView summary={state.data} />;

  return (
    <>
      <form
        className="research-toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          if (!labelValid || !rangeValid) return;
          if (!draftChanged) reload();
          else setFilter({ studyLabel: labelDraft, from: fromDraft, to: toDraft });
        }}
      >
        <div className="inline-field">
          <label htmlFor={labelId}>{t('research.study')}</label>
          <input
            id={labelId}
            value={labelDraft}
            onChange={(e) => setLabelDraft(e.target.value.trim().toLowerCase())}
            aria-invalid={labelValid ? undefined : true}
            aria-describedby={`${labelId}-hint`}
            spellCheck={false}
          />
        </div>
        <span id={`${labelId}-hint`} className="muted small">
          {t('research.studyHint', { label: RESEARCH_CATALOG.studyLabel })}
        </span>
        <div className="inline-field">
          <label htmlFor={fromId}>{t('research.from')}</label>
          <input
            id={fromId}
            type="date"
            value={fromDraft}
            onChange={(e) => setFromDraft(e.target.value)}
            aria-invalid={rangeValid ? undefined : true}
            data-testid="research-from"
          />
        </div>
        <div className="inline-field">
          <label htmlFor={toId}>{t('research.to')}</label>
          <input
            id={toId}
            type="date"
            value={toDraft}
            onChange={(e) => setToDraft(e.target.value)}
            aria-invalid={rangeValid ? undefined : true}
            data-testid="research-to"
          />
        </div>
        <button type="submit" className="btn btn--secondary" disabled={!labelValid || !rangeValid}>
          {t('research.refresh')}
        </button>
        <button type="button" className="btn btn--ghost" onClick={onForget}>
          {t('research.forget')}
        </button>
      </form>

      {body}

      <section className="card" aria-labelledby="research-downloads">
        <h2 id="research-downloads">{t('research.downloads')}</h2>
        <p className="muted small">{t('research.downloadsNote')}</p>
        <div className="button-row">
          {EXPORTS.map((kind) => (
            <button
              key={kind}
              type="button"
              className="btn btn--secondary"
              disabled={downloading !== null}
              onClick={() => void download(kind)}
              data-testid={`research-export-${kind}`}
            >
              {t(`research.export.${kind}`)} (CSV)
            </button>
          ))}
        </div>
      </section>
    </>
  );
}

function metricTitle(m: ResearchMetricValue, locale: Locale, roleLabel: (r: ResearchRole) => string): string {
  const title = catalogText(findCatalogMetric(m.id)?.title, locale, m.id);
  const base = m.id.startsWith('H') ? `${m.id.split('_')[0]} · ${title}` : title;
  return m.id === 'SUS' && m.role ? `${base} — ${roleLabel(m.role)}` : base;
}

function metricValue(m: ResearchMetricValue, locale: Locale): string | null {
  if (m.value === null) return null;
  return m.id === 'SUS' ? formatDecimal(m.value, locale) : formatPercent(m.value, locale);
}

/** The summary tables (separate for tests). */
export function ResearchSummaryView({ summary }: { summary: ResearchSummary }) {
  const { t, locale } = useI18n();
  const roleLabel = (r: ResearchRole): string => t(`research.role.${r}`);
  const noData = t('research.noData');

  return (
    <>
      <section aria-labelledby="research-sessions">
        <h2 id="research-sessions">{t('research.sessions')}</h2>
        <div className="stats">
          {ROLES.map((r) => (
            <StatCard key={r} label={roleLabel(r)} value={summary.sessions[r] ?? 0} testId={`sessions-${r}`} />
          ))}
        </div>
      </section>

      <section className="card" aria-labelledby="research-hypotheses">
        <h2 id="research-hypotheses">{t('research.hypotheses')}</h2>
        <div className="table-wrap">
          <table className="table" data-testid="research-metrics">
            <thead>
              <tr>
                <th scope="col">{t('research.col.metric')}</th>
                <th scope="col" className="num">
                  {t('research.col.value')}
                </th>
                <th scope="col" className="num">
                  {t('research.col.n')}
                </th>
                <th scope="col" className="num">
                  {t('research.col.threshold')}
                </th>
                <th scope="col">{t('research.col.outcome')}</th>
              </tr>
            </thead>
            <tbody>
              {summary.metrics.map((m) => (
                <tr key={`${m.id}-${m.role ?? ''}`} data-metric={m.id} data-pass={String(m.pass)}>
                  <th scope="row">{metricTitle(m, locale, roleLabel)}</th>
                  <td className="num strong">{metricValue(m, locale) ?? noData}</td>
                  <td className="num">{m.n}</td>
                  <td className="num">{m.threshold !== null ? `≥ ${formatPercent(m.threshold, locale)}` : '—'}</td>
                  <td>
                    {m.threshold === null ? (
                      '—'
                    ) : m.pass === null ? (
                      <Chip tone="neutral">{noData}</Chip>
                    ) : m.pass ? (
                      <Chip tone="success">{t('research.pass')}</Chip>
                    ) : (
                      <Chip tone="danger">{t('research.fail')}</Chip>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card" aria-labelledby="research-tasks">
        <h2 id="research-tasks">{t('research.tasks')}</h2>
        <div className="table-wrap">
          <table className="table" data-testid="research-tasks">
            <thead>
              <tr>
                <th scope="col">{t('research.col.task')}</th>
                <th scope="col" className="num">
                  n
                </th>
                <th scope="col" className="num">
                  {t('research.col.success')}
                </th>
                <th scope="col" className="num">
                  {t('research.col.median')}
                </th>
                <th scope="col" className="num">
                  {t('research.col.p90')}
                </th>
                <th scope="col" className="num">
                  {t('research.col.target')}
                </th>
              </tr>
            </thead>
            <tbody>
              {summary.tasks.map((task) => {
                const cat = findCatalogTask(task.taskId);
                return (
                  <tr key={task.taskId} data-task={task.taskId}>
                    <th scope="row">
                      <span className="mono">{task.taskId}</span> · {catalogText(cat?.title, locale, task.taskId)}
                      {cat ? <span className="muted small"> ({roleLabel(cat.role)})</span> : null}
                    </th>
                    <td className="num">{task.n}</td>
                    <td className="num">
                      {task.successRate !== null ? formatPercent(task.successRate, locale) : noData}
                    </td>
                    <td className="num">
                      {task.medianSeconds !== null ? formatSeconds(task.medianSeconds, locale) : '—'}
                    </td>
                    <td className="num">{task.p90Seconds !== null ? formatSeconds(task.p90Seconds, locale) : '—'}</td>
                    <td className="num">
                      {cat?.targetSeconds !== undefined ? `≤ ${formatSeconds(cat.targetSeconds, locale)}` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="research-sus">
        <h2 id="research-sus">{t('research.sus')}</h2>
        <p className="muted small">{t('research.susNote')}</p>
        <div className="stats">
          {ROLES.map((r) => {
            const score = summary.sus[r];
            return (
              <StatCard
                key={r}
                label={roleLabel(r)}
                value={typeof score === 'number' ? formatDecimal(score, locale) : noData}
                testId={`sus-${r}`}
              />
            );
          })}
        </div>
      </section>
    </>
  );
}
